import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { HoursLogContext } from "../../../context/page/HoursLogContext";

import {
  getWeeksAgoText,
  getDateString,
  formatDate,
} from "../../../utils/datetime";

import { NavigationButton } from "../../../components/ui";

import { Check } from "../../../components/icons/svg/outline";

/**
 * WeeklyChecklist Component
 * Displays a weekly overview checklist showing all 7 days
 * with status indicators (currently showing Monday as highlighted)
 */
const WeeklyChecklist = ({ logs }) => {
  const navigate = useNavigate();
  const {
    date,
    setDate,
    weekStart,
    weekEnd,
    goToPrevWeek,
    goToNextWeek,
    goToPrevDay,
    goToNextDay,
  } = useContext(WeeklyContext);
  const { wholeWeeks } = useContext(HoursLogContext);
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Required to keep the URL in sync with the selected date on week change
  useEffect(() => {
    if (date !== weekStart) {
      navigate(`/timekeeping/hours-log/${getDateString(date)}`);
    }
  }, [date, weekStart, navigate]);

  return (
    <div className="bg-gray-200 rounded-lg shadow-md mb-6 p-4">
      {/* Grid container - 3 columns, 2 rows */}
      <div className="grid grid-cols-12 gap-4">
        {/* Top Row - Week Navigation */}
        {/* Previous Week Button */}
        <div className="flex justify-center items-center">
          <NavigationButton
            direction="left"
            double
            onClick={goToPrevWeek}
            variant="primary"
          />
        </div>

        {/* Current Week Date Range */}
        <div className="text-center flex items-center justify-center col-span-10">
          <div className="font-bold text-lg text-gray-800">
            {weekStart.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
            {" - "}
            {weekEnd.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}{" "}
            ({getWeeksAgoText(wholeWeeks)})
          </div>
        </div>

        {/* Next Week Button */}
        <div className="flex justify-center items-center">
          <NavigationButton
            direction="right"
            double
            onClick={goToNextWeek}
            variant="primary"
          />
        </div>

        {/* Bottom Row - Day Navigation */}
        {/* Previous Day Button */}
        <div className="flex justify-center items-center">
          <NavigationButton
            direction="left"
            onClick={goToPrevDay}
            variant="secondary"
          />
        </div>

        {/* Days of Week Grid */}
        <div className="grid grid-cols-7 gap-2 text-center col-span-10">
          {daysOfWeek.map((day, index) => (
            <button
              key={day}
              className={`p-2 rounded hover:shadow-md transition-colors duration-200 ${
                index === date.getDay()
                  ? "bg-green-200 border-2 border-green-400"
                  : "bg-white hover:bg-gray-100"
              }`}
              onClick={() => {
                setDate(
                  new Date(date.setDate(date.getDate() + index - date.getDay()))
                );
              }}
            >
              <div className="font-semibold text-sm">
                {/* Calc day from date (static) plus index (dynamic) */}
                {(() => {
                  const dayDate = new Date(date);
                  dayDate.setDate(date.getDate() + index - date.getDay());
                  return formatDate(dayDate, "day month dd");
                })()}
              </div>
              {Array.isArray(logs) && logs[index]?.length > 0 ? (
                <div className="text-xs flex items-center justify-center text-green-800">
                  <Check height={16} width={16} />
                </div>
              ) : (
                <div className="text-xs text-gray-500">---</div>
              )}
            </button>
          ))}
        </div>

        {/* Next Day Button */}
        <div className="flex justify-center items-center">
          <NavigationButton
            direction="right"
            onClick={goToNextDay}
            variant="secondary"
          />
        </div>
      </div>
    </div>
  );
};

export default WeeklyChecklist;
